import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from './cn';

/**
 * Button — Design System v2 (docs/design-system-v2.html §5.btn).
 *
 * Variantes: primary | secondary | ghost | danger | success
 * Tamanhos: sm | md (default) | lg
 *
 * - `loading=true` desabilita o botão e troca o ícone esquerdo por
 *   um spinner SVG animado, mantendo a largura do botão estável.
 * - `icon` aceita qualquer ReactNode (typical: ícone do `@tabler/icons-react`).
 * - Herda toda a API de `<button>` (type, onClick, aria-*, …).
 */

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: ReactNode;
  children?: ReactNode;
}

const BASE =
  'inline-flex items-center justify-center gap-2 rounded-md font-body font-semibold whitespace-nowrap leading-none transition-all border border-transparent disabled:opacity-50 disabled:cursor-not-allowed active:translate-y-[0.5px]';

// `lg` = 46px segue `docs/design/login_mockup.html` §.btn-primary (altura do
// botão "Entrar" do split-screen). Demais tamanhos seguem ADR-020.
const SIZE: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-[13px]',
  md: 'h-10 px-[17px] text-[14px]',
  lg: 'h-[46px] px-6 text-[15px]',
};

const VARIANT: Record<ButtonVariant, string> = {
  primary:
    'bg-primary text-on-primary shadow-[0_1px_2px_rgba(20,179,163,0.3)] hover:bg-primary-hover hover:-translate-y-px hover:shadow-[0_4px_14px_rgba(20,179,163,0.35)] disabled:hover:translate-y-0 disabled:hover:shadow-none',
  secondary:
    'bg-bg-surface-v2 text-text-primary border-border-strong hover:bg-bg-inset hover:border-text-faint-v2',
  ghost: 'bg-transparent text-text-secondary hover:bg-bg-muted hover:text-text-primary',
  danger:
    'bg-danger-v2 text-white hover:brightness-95 hover:-translate-y-px disabled:hover:translate-y-0',
  success:
    'bg-success-v2 text-white hover:brightness-95 hover:-translate-y-px disabled:hover:translate-y-0',
};

const Spinner = ({ size }: { size: ButtonSize }): JSX.Element => {
  // 15px sm, 17px md, 18px lg — bate com o tamanho dos ícones esperado.
  const pixels = size === 'sm' ? 15 : size === 'lg' ? 18 : 17;
  return (
    <svg
      width={pixels}
      height={pixels}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className="animate-spin"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2.5" opacity="0.25" />
      <path
        d="M22 12a10 10 0 0 1-10 10"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading = false, disabled, icon, children, className, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(BASE, SIZE[size], VARIANT[variant], className)}
      {...rest}
    >
      {loading ? <Spinner size={size} /> : icon}
      {children}
    </button>
  );
});
